import 'whatwg-fetch';
import { AppError } from 'src/shared/components/ErrorBoundary';
import logger from '../lib/logger';
import { formatLocalUrlTemplate } from '../lib/utils/stringUtils';

export const getBackendBaseUrl = () => {
  // If environment is local, url will be the same as the front end
  if (process.env.ENVIRONMENT === 'local') {
    return formatLocalUrlTemplate(process.env.BACKEND_BASE_URL as string);
  }

  return `${process.env.BACKEND_BASE_URL}`;
};

export const callExternalEndpoint = async <T>(endpoint: string, method = 'POST'): Promise<T> => {
  const data = await fetch(endpoint, {
    method,
  });

  const json = await data.json();
  logger.info(`App: api ${endpoint} - ${JSON.stringify(json)}`);

  return json as unknown as T;
};

export const callEndpoint = async <T>(endpoint: string, jwt: string = '', method = 'POST'): Promise<T> => {
  try {
    const telehealthBaseURL = getBackendBaseUrl();
    const data = await fetch(`${telehealthBaseURL}/${endpoint}`, {
      method,
      ...(jwt && {
        headers: new Headers({
          Authorization: `Bearer ${jwt}`,
        }),
      }),
    });

    if (!data.ok) {
      let errorMsg = `HTTP Status ${data.status} - ${data.statusText}`;
      const bodyText = await data.text();
      if (bodyText) {
        errorMsg += ` / ${bodyText}`;
      }

      throw new Error(errorMsg);
    }

    const json = await data.json();
    logger.info(`App: api ${endpoint} - ${JSON.stringify(json)}`);
    // The shape of the response payload from the various backend endpoints is inconsistent.
    // /appointment, /brand, /medicalgroup, and /join-session
    //    The response payload for these endpoints exists in the main level of the json object
    // /start-video, /join-chat, /one-way-message, and /active-participants
    //    The response payload for these endpoints exists in "response" property of the json object
    return (json.response || json) as unknown as T;
  } catch (error) {
    const [mainEndpoint, queryString] = endpoint.split('?');
    throw new AppError(`Failed API - ${mainEndpoint} - ${(error as Error).message}`, {
      additionalInfo: {
        ...(queryString && { queryString }),
      },
    });
  }
};

export const callEndpointWithRetry = async <T>(
  endpoint: string,
  jwt: string,
  method = 'POST',
  retryCount = 4
): Promise<T> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return callEndpoint<T>(endpoint, jwt, method).catch((error) => {
    // at this point, we've retried enough. Just throw the last error caught
    if (retryCount < 1) {
      error.message = error.message.replace('Failed API', 'Failed API w/ retries');
      throw error;
    }

    // otherwise, try again
    return callEndpointWithRetry(endpoint, jwt, method, retryCount - 1);
  });
};

export const calcTimeToLive = (exp: number) => {
  const expirationDateTimeInMs = exp * 1000; // time of chat expiration, in ms
  const now = Date.now(); // in ms
  const ttl = expirationDateTimeInMs - now; // time to live in ms, can divide by 60000 to get minutes till expiration (dimensional analysis says X ms * 1s/1000ms * 1min/60s will convert to equivalent minutes)

  return ttl;
};

const TenMinutes = 60 * 1000 * 10;
export const setRefreshTimer = (exp: number, callback: () => void) => {
  let timeout: number;
  let timerId: NodeJS.Timeout | null = null;
  const ttl = calcTimeToLive(exp);
  if (ttl >= TenMinutes + 20000) {
    // 10min * 60s/min * 1000ms/s converts to ms.
    // If the time-to-live is greater than 10 min, set the timeout for 10min before expiration.
    // Said another way, if we set a timer right now for the value ttl, which is the amount of time between now & expiration `setTimeout(() => {}, ttl)`, the timer would go off right when the jwt expires
    // We'd like it to go off 10 minutes prior.
    // Start with the "time to live" (or "time until expiration") and subtract 10 min.
    // The computed value is the amount of time between NOW and a mark 10 minutes before the jwt expires, and therefore is the desired parameter for our `setTimeout()`
    // The +20s is to make sure that we don't accidentally compute a timeout that is less than 20000ms and thereby flunk out on the `timeout >= 20000 && setTimeout(()...` check down below
    timeout = ttl - TenMinutes;
  } else {
    timeout = ttl / 2;
  }
  if (timeout >= 20000) {
    // only set the timer to try to refresh if the timer would be longer than 20s, so as not to overwhelm the backend
    timerId = setTimeout(() => {
      callback();
    }, timeout);
  }

  return timerId;
};
